/**
 * As funções puras da mídia de WhatsApp: formatar, rotular e montar a URL (8.4, FR-41, UX-DR7).
 *
 * Nada aqui chama rede, monta componente nem guarda estado — e a separação é o que torna a suíte
 * barata: os rótulos do protótipo (`Áudio · 0:22`, `📄 Proposta_ValeVerde_12884.pdf · 1,2 MB`) são
 * quatro regras de formatação, e montar a tela inteira para afirmá-las custaria caro para nada.
 *
 * **Os números são escritos como se escreve em português**: vírgula decimal, e não ponto. `1,2 MB`
 * é o que o protótipo mostra, e `1.2 MB` é o que um `toFixed(1)` cru produziria — o mesmo erro que
 * o resto do SPA já evita usando `Intl` em vez de concatenar string.
 *
 * **A URL da mídia é montada aqui, e não no componente**, pela razão de sempre: ela é a mesma em
 * quatro lugares (a imagem, o vídeo, o áudio e o link de baixar), e escrita quatro vezes a primeira
 * a divergir seria a do download. Ela passa por `montarUrl`, o mesmo lugar por onde toda chamada do
 * SPA passa — um caminho montado à mão aqui pularia o prefixo do gateway.
 */

import { montarUrl } from '@/api/cliente'
import type { Conversa, Mensagem, TipoDeMidia } from './api'

/** O rótulo de cada tipo, como o protótipo os escreve. */
export const ROTULO_DO_TIPO: Record<TipoDeMidia, string> = {
  documento: 'Documento',
  imagem: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
}

/** O ícone de documento do protótipo (`:869`). Ele é decorativo — o nome é que informa. */
export const ICONE_DE_DOCUMENTO = '📄'

/** O ícone de áudio do protótipo (`:872`), pelo mesmo critério. */
export const ICONE_DE_AUDIO = '🎙'

/** O que a prévia mostra quando a última mensagem é uma mídia sem nome e sem duração. */
export const PREVIA_DE_ARQUIVO = 'Arquivo'

/** O rótulo do botão que baixa o arquivo. */
export const BAIXAR = 'Baixar'

/**
 * O nome acessível do link de baixar — o rótulo da mídia junto com a ação.
 *
 * Ele existe porque um chat com cinco anexos gera cinco links cujo nome visível é o mesmo `Baixar`,
 * e quem navega por links num leitor de tela ouve "Baixar, Baixar, Baixar" sem nada que os separe.
 * O texto **visível** continua sendo `Baixar` (a coluna é estreita, e o rótulo da mídia já está ao
 * lado); o que muda é o `aria-label`.
 */
export const baixarDescrito = (rotulo: string | null): string =>
  rotulo === null ? BAIXAR : `${BAIXAR}: ${rotulo}`

/** O que o leitor de tela ouve no lugar de uma imagem que não tem legenda. */
export const IMAGEM_SEM_LEGENDA = 'Imagem recebida na conversa'

/**
 * O rótulo da mensagem que levou a foto do Orçamento do CRM (13.22c1): o fato do envio, que a SDR
 * também lê. O arquivo, ela não recebe — o servidor tira a mídia da resposta dela (T7).
 */
export const rotuloDoOrcamentoEnviado = (numero: number): string =>
  `Orçamento nº ${String(numero)} enviado`

/**
 * `0:22`, `1:05`, `12:03` — a duração como o WhatsApp a escreve.
 *
 * **Minutos sem zero à esquerda e segundos com dois dígitos**: é a convenção de todo player, e
 * `0:5` seria lido como cinco décimos por qualquer pessoa.
 *
 * Acima de uma hora ela ganha o terceiro campo (`1:02:03`). Um áudio de WhatsApp não chega lá, mas
 * um vídeo chega — e `62:03` seria um número que ninguém sabe ler de imediato.
 *
 * Valor ausente, negativo ou que não é número vira `null`: a tela então não desenha a duração, em
 * vez de escrever `NaN:aN` ao lado do player.
 */
export function formatarDuracao(segundos: number | null | undefined): string | null {
  if (segundos === null || segundos === undefined || !Number.isFinite(segundos) || segundos < 0) {
    return null
  }
  const inteiros = Math.floor(segundos)
  const s = inteiros % 60
  const minutosTotais = Math.floor(inteiros / 60)
  const m = minutosTotais % 60
  const h = Math.floor(minutosTotais / 60)
  const doisDigitos = (valor: number) => String(valor).padStart(2, '0')
  return h > 0 ? `${h}:${doisDigitos(m)}:${doisDigitos(s)}` : `${m}:${doisDigitos(s)}`
}

/** Uma casa decimal, com **vírgula** — o separador do português (protótipo: `1,2 MB`). */
const UMA_CASA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** Sem casa decimal, para os bytes e os KB: `847 KB`, e não `847,0 KB`. */
const SEM_CASA = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })

/**
 * `1,2 MB`, `847 KB`, `512 B` — o tamanho como o protótipo o escreve.
 *
 * **A base é 1000, e não 1024**, e a escolha é deliberada: é o que o sistema operacional mostra a
 * quem acabou de baixar o arquivo, e um `1,1 MB` na tela ao lado de `1,2 MB` no gerenciador de
 * arquivos é a diferença entre alguém confiar no número e alguém desconfiar do CRM.
 *
 * **MB e GB ganham uma casa decimal; B e KB não.** `1,2 MB` informa; `847,0 KB` só ocupa espaço, e
 * `512,0 B` é ruído. Acima de GB a escala para: um anexo de WhatsApp não chega a TB, e o teto do
 * serviço são dezenas de MB.
 *
 * **A escala é escolhida depois do arredondamento**, e não antes: `999_999` bytes arredondam para
 * mil KB, e `1.000 KB` é um número que ninguém escreve — o certo é `1,0 MB`. O mesmo vale na
 * fronteira de MB para GB.
 *
 * Valor ausente ou inválido vira `null`, pela razão de `formatarDuracao`.
 */
export function formatarTamanho(bytes: number | null | undefined): string | null {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
    return null
  }
  /*
   * **A escala é escolhida pelo valor já arredondado, e não pelo cru** — e essa é a correção de um
   * erro que só aparecia na fronteira. Comparando o cru, `999_999` caía em KB, virava `999,999` e
   * era arredondado para `1.000 KB`; `999_999_999` saía `1.000,0 MB`. Os dois são números que
   * ninguém escreve: quem chega em mil de uma unidade já está na seguinte.
   *
   * Arredondar primeiro e comparar depois fecha isso em qualquer degrau: o que decide é o que a
   * pessoa vai ler, e não o que a divisão produziu.
   */
  const escalas = [
    { divisor: 1, sufixo: 'B', formato: SEM_CASA },
    { divisor: 1_000, sufixo: 'KB', formato: SEM_CASA },
    { divisor: 1_000_000, sufixo: 'MB', formato: UMA_CASA },
    { divisor: 1_000_000_000, sufixo: 'GB', formato: UMA_CASA },
  ] as const

  for (const escala of escalas) {
    const valor = bytes / escala.divisor
    const arredondado = Number(escala.formato.format(valor).replace('.', '').replace(',', '.'))
    // A última escala não tem para onde subir: um arquivo em TB não existe neste CRM (o teto do
    // serviço são dezenas de MB), e `1.234,5 GB` continua sendo legível.
    if (arredondado < 1000 || escala === escalas[escalas.length - 1]) {
      return `${escala.formato.format(valor)} ${escala.sufixo}`
    }
  }
  /* v8 ignore next 2 -- o laço sempre devolve: a última escala é incondicional. */
  return null
}

/**
 * O rótulo de uma mídia numa linha — os literais do protótipo (`:856`, `:869`, `:872`).
 *
 * Três formas, e cada uma é o que aquele tipo tem de dizer:
 *
 * - **áudio**: `🎙 Áudio · 0:22`. O que importa é quanto tempo dura, e não que arquivo é;
 * - **documento**: `📄 Proposta_ValeVerde_12884.pdf · 1,2 MB`. O que importa é o nome e o peso —
 *   é por eles que alguém decide se abre agora ou depois;
 * - **imagem e vídeo**: o rótulo do tipo, com o tamanho quando ele existe. O conteúdo é a própria
 *   miniatura na conversa, e um nome de arquivo ali não acrescenta nada.
 *
 * O separador é ` · `, literal do protótipo, e os campos que faltam **somem** em vez de virar
 * `undefined`: uma mídia sem duração conhecida mostra `🎙 Áudio`, e não `🎙 Áudio · `.
 */
export function rotuloDaMidia(dados: {
  tipo: TipoDeMidia
  nome: string | null | undefined
  tamanho: number | null | undefined
  duracao: number | null | undefined
}): string {
  const partes: string[] = []
  if (dados.tipo === 'audio') {
    partes.push(`${ICONE_DE_AUDIO} ${ROTULO_DO_TIPO.audio}`)
    const duracao = formatarDuracao(dados.duracao)
    if (duracao !== null) {
      partes.push(duracao)
    }
    return partes.join(' · ')
  }
  if (dados.tipo === 'documento') {
    const nome = dados.nome ?? PREVIA_DE_ARQUIVO
    partes.push(`${ICONE_DE_DOCUMENTO} ${nome}`)
    const tamanho = formatarTamanho(dados.tamanho)
    if (tamanho !== null) {
      partes.push(tamanho)
    }
    return partes.join(' · ')
  }
  partes.push(ROTULO_DO_TIPO[dados.tipo])
  const duracao = dados.tipo === 'video' ? formatarDuracao(dados.duracao) : null
  if (duracao !== null) {
    partes.push(duracao)
  }
  const tamanho = formatarTamanho(dados.tamanho)
  if (tamanho !== null) {
    partes.push(tamanho)
  }
  return partes.join(' · ')
}

/**
 * O rótulo da prévia de uma conversa cuja última mensagem é mídia — ou `null` quando não é.
 *
 * **A legenda vence o rótulo do arquivo**, e a ordem é a do próprio WhatsApp: quem mandou um PDF
 * com um "segue a proposta" espera ver a frase na lista, e não o nome do arquivo. Sem legenda, o
 * rótulo da mídia é o que sobra — e ele é exatamente o do protótipo (`Áudio · 0:22`).
 */
export function rotuloDaPrevia(conversa: Conversa): string | null {
  //: 13.22c1: a última mensagem levou a foto do Orçamento do CRM — o rótulo é o do envio, para
  //: todo perfil (a SDR recebe a prévia sem a mídia).
  const numeroDoOrcamento = conversa.previa_numero_do_orcamento_do_crm ?? null
  if (numeroDoOrcamento !== null) {
    return rotuloDoOrcamentoEnviado(numeroDoOrcamento)
  }
  if (conversa.previa_tipo_de_midia === null) {
    return null
  }
  if (conversa.previa !== null && conversa.previa !== '') {
    return conversa.previa
  }
  return rotuloDaMidia({
    tipo: conversa.previa_tipo_de_midia,
    nome: conversa.previa_nome_do_arquivo,
    tamanho: conversa.previa_tamanho_da_midia,
    duracao: conversa.previa_duracao_em_segundos,
  })
}

/** O rótulo de uma mensagem de mídia dentro do chat — a mesma regra, sobre a mensagem. */
export function rotuloDaMensagem(mensagem: Mensagem): string | null {
  if (mensagem.tipo_de_midia === null) {
    return null
  }
  return rotuloDaMidia({
    tipo: mensagem.tipo_de_midia,
    nome: mensagem.nome_do_arquivo,
    tamanho: mensagem.tamanho_da_midia,
    duracao: mensagem.duracao_em_segundos,
  })
}

/**
 * O tipo de um arquivo escolhido no disco, a partir do mime que o navegador declarou.
 *
 * **Ela decide pelo prefixo, e o resto é documento.** O prefixo (`image/`, `video/`, `audio/`) é o
 * que o navegador promete para qualquer arquivo reconhecido, e a lista fechada de formatos aceitos
 * é do **serviço** — não desta função. A divisão importa: um `image/svg+xml` escolhido aqui é
 * mandado como `imagem` e **recusado pelo serviço**, com a mensagem em PT-BR chegando à tela como
 * qualquer outra recusa. Reimplementar a lista aqui daria duas respostas para a mesma pergunta, e a
 * do SPA seria a que ninguém revisa.
 *
 * Arquivo sem mime declarado (acontece com extensões que o sistema não conhece) vira `documento`,
 * que é o tipo que aceita mais formatos e o único desfecho honesto: o CRM não abre o arquivo para
 * descobrir o que ele é.
 */
export function tipoDoArquivo(mime: string): TipoDeMidia {
  if (mime.startsWith('image/')) {
    return 'imagem'
  }
  if (mime.startsWith('video/')) {
    return 'video'
  }
  if (mime.startsWith('audio/')) {
    return 'audio'
  }
  return 'documento'
}

/**
 * O teto de tamanho de um anexo, em bytes — **o mesmo do serviço**.
 *
 * Ele é uma cópia declarada, como `TAMANHO_MAXIMO_DO_TEXTO` em `Conversas.tsx` e pela mesma razão:
 * o `openapi.json` não publica a faixa de um limite de corpo, e sem ele a única forma de a pessoa
 * descobrir que o arquivo é grande demais seria **esperar o upload inteiro** para ler a recusa. Com
 * um arquivo de 80 MB numa conexão de escritório, isso é mais de um minuto olhando "Enviando…".
 *
 * A recusa do serviço continua sendo a que vale: esta é a que evita a espera.
 */
export const TAMANHO_MAXIMO_DA_MIDIA = 32 * 1024 * 1024

/** O teto em MB, para a frase — derivado, e não digitado duas vezes. */
export const TAMANHO_MAXIMO_DA_MIDIA_EM_MB = TAMANHO_MAXIMO_DA_MIDIA / (1024 * 1024)

/** A recusa local do teto. Ela **nomeia o teto**, como a do serviço. */
export const arquivoGrandeDemais = (): string =>
  `O arquivo passa do limite de ${TAMANHO_MAXIMO_DA_MIDIA_EM_MB} MB. ` +
  'Mande um arquivo menor, ou divida o envio.'

/**
 * Os formatos que o `<input type="file">` oferece — o `accept` do seletor.
 *
 * Ele existe para que o seletor do sistema já mostre o que serve: sem ele, escolher um `.svg` ou um
 * `.exe` é um caminho normal que só termina em recusa depois do upload.
 *
 * **Ele é uma sugestão, e não a regra**, e por isso é uma lista de famílias e não a lista fechada de
 * mimes do serviço: `accept` é contornável (o seletor tem "todos os arquivos", e um arrastar-e-
 * soltar o ignora), e duplicar a lista fechada aqui daria duas respostas para a mesma pergunta —
 * com a do SPA sendo a que ninguém revisa. Quem decide continua sendo `infra/midia.py`.
 */
export const FORMATOS_ACEITOS = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip'

/**
 * A URL dos bytes daquela mensagem — o `src` do player e o `href` do link de baixar.
 *
 * Ela passa por `montarUrl`, o mesmo lugar por onde toda chamada do SPA passa: um caminho montado à
 * mão aqui pularia o prefixo `/api/comunicacao` do gateway, e o sintoma seria um `404` do Caddy que
 * não se parece com nada.
 *
 * **Ela não carrega token nenhum**, e a ausência é o desenho: quem recorta é a RLS do serviço, com
 * o cookie da sessão que o navegador manda sozinho. Um token na URL seria um link que funciona fora
 * da sessão — exatamente o que o AD-9 recusa.
 *
 * `anexo` força o download (`Content-Disposition: attachment`) do que, sem ele, abriria na própria
 * página: é o que o botão "Baixar" usa para uma imagem ou um PDF.
 */
export function urlDaMidia(mensagemId: string, opcoes: { anexo?: boolean } = {}): string {
  const caminho = `/midias/${encodeURIComponent(mensagemId)}`
  const url = montarUrl('comunicacao', caminho)
  return opcoes.anexo === true ? `${url}?anexo=1` : url
}
